import React, { lazy } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PageContainer } from '../../shared/Styled/Styled';
import {
  useAllRestaurants,
  useRestaurantExceptionHours,
  useRestaurantOpeningHours,
  useRestaurantStatus,
} from '../../apis/shops-api';
import LoadingIndicator from '../../shared/LoadingIndicator';
import {
  ChannelsCard,
  OutOfStockCard,
  PaymentMethodsCard,
  PreordersCard,
  RestaurantInfoCard,
  StatusHeader,
  TodayCard,
} from './cards';
import { getEffectiveStatus, getStatusDetail, getStatusView, getTodaysOpeningHours } from './utils';
import { isForbiddenError } from '../../shared/utils/apiErrors';
import { useUserPermissions } from '../../apis/user-details';
import { isTestStatesRoute } from './testStatesRoute';

// Loaded only for `/9999?testPage`: the harness is hundreds of lines of fixtures nobody else needs.
const RestaurantOverviewTestStates = lazy(() => import('./testStates'));

const RestaurantOverview: React.FC = () => {
  const { restaurant: restaurantId } = useParams<{ restaurant: string }>();

  const { t } = useTranslation();
  const { shops } = useUserPermissions();
  const { data: restaurants, isLoading: restaurantsLoading, error: restaurantsError } = useAllRestaurants();
  const restaurant = restaurants?.find((r) => r.shopExternalId === restaurantId);

  const { data: status, isInitialLoading: statusLoading } = useRestaurantStatus(restaurantId || '');
  // For the header's "open today" chip. The weekly hours are already fetched by the channels and
  // today cards, so react-query serves this from the same cache; the exceptions are a new request.
  const { data: openingHours } = useRestaurantOpeningHours(restaurantId || '');
  const { data: exceptionHours } = useRestaurantExceptionHours(restaurantId || '');

  // Gate only on what drives the first meaningful paint: the restaurant identity (and the
  // not-found case) and the header status pill. The KPI grid, payment/out-of-stock cards and the
  // menu chip all render graceful loading/empty states, so they fill in progressively rather than
  // holding the whole page behind the slowest query.
  if (restaurantsLoading || statusLoading) {
    return <LoadingIndicator />;
  }

  // Checked before "not found": without the restaurant list there is no way to know whether this
  // shop exists, and telling someone their restaurant does not exist when they simply may not see it
  // sends them hunting for the wrong problem.
  if (isForbiddenError(restaurantsError)) {
    return (
      <NotFound>
        <strong>{t('permissions.notAuthorizedTitle')}</strong>
        <div>{t('permissions.noDataAccess')}</div>
      </NotFound>
    );
  }

  if (!restaurant) {
    return <NotFound>{t('restaurant.payments.restaurantNotFoundInRPH')}</NotFound>;
  }

  const statusView = getStatusView(getEffectiveStatus(status));

  const statusDetail = getStatusDetail(status, t);

  return (
    <PageContainer>
      <StatusHeader
        restaurant={restaurant}
        // Back to the overview only for someone who has several restaurants: for anyone else `/` is this
        // page, or nothing.
        backTo={shops.length > 1 ? '/' : undefined}
        statusLabel={t(statusView.labelKey)}
        statusTone={statusView.tone}
        statusIcon={statusView.icon}
        statusDetail={statusDetail}
        todaysHours={getTodaysOpeningHours(openingHours, exceptionHours)}
      />
      <ChannelsCard restaurant={restaurant} />
      {/* Above the day's figures: a payments problem outranks how the day is going. */}
      <PaymentMethodsCard restaurant={restaurant} />
      <PreordersCard restaurant={restaurant} />
      <TodayCard restaurant={restaurant} />

      {/* The calm half of the page: out-of-stock is always shown (empty, it is the shortcut for
          adding one) and the information card keeps it company rather than leaving a blank column. */}
      <CardGrid>
        <OutOfStockCard restaurant={restaurant} />
        <RestaurantInfoCard restaurant={restaurant} />
      </CardGrid>
    </PageContainer>
  );
};

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing(2)};

  ${({ theme }) => theme.breakpoints.up('md')} {
    grid-template-columns: repeat(2, 1fr);
    align-items: start;
  }
`;

const NotFound = styled.div`
  padding: ${({ theme }) => theme.spacing(4)};
  text-align: center;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

// `/9999?testPage` swaps the dashboard for the mock state harness. Done in a wrapper rather than
// inside RestaurantOverview so the real page — and its queries for a shop that does not exist — never
// mounts, and so neither component ends up calling hooks conditionally. The route's Suspense in
// AppRoutes covers the harness chunk loading.
const RestaurantOverviewRoute: React.FC = () => {
  const { restaurant: restaurantId } = useParams<{ restaurant: string }>();
  const { search } = useLocation();

  if (isTestStatesRoute(restaurantId, search)) {
    return <RestaurantOverviewTestStates />;
  }

  return <RestaurantOverview />;
};

export default RestaurantOverviewRoute;
